"use client";

import { useAuth } from '@/contexts/AuthContext';
import type { Listing } from '@/services/api';
import { EVENT_TYPES } from '@/utils/constants';
import { evaluateDealType } from '@/utils/dealEvaluation';
import { logEvent } from '@/utils/helpers/analyticsHelpers';
import React, { createContext, useCallback, useContext, useEffect, useRef } from 'react';

interface ImpressionConfig {
    threshold: number;
    minVisibleTime: number;
    metadata: Record<string, unknown>;
}

interface TrackedElement {
    element: HTMLElement;
    listing: Listing;
    config: ImpressionConfig;
    visibilityTimer: NodeJS.Timeout | null;
    visibilityStartTime: number | null;
    hasTracked: boolean;
}

interface GlobalImpressionContextType {
    trackElement: (element: HTMLElement, listing: Listing, config: ImpressionConfig) => () => void;
    getTrackedElementsCount: () => number;
}

const GlobalImpressionContext = createContext<GlobalImpressionContextType | undefined>(undefined);

const INTERSECTION_THRESHOLDS = [0.1, 0.25, 0.5, 0.75, 1.0];

export function GlobalImpressionProvider({ children }: { children: React.ReactNode }) {
    const { user } = useAuth();
    const observerRef = useRef<IntersectionObserver | null>(null);
    const trackedElementsRef = useRef<Map<HTMLElement, TrackedElement>>(new Map());

    const clearVisibilityTimer = useCallback((tracked: TrackedElement) => {
        if (tracked.visibilityTimer) {
            clearTimeout(tracked.visibilityTimer);
            tracked.visibilityTimer = null;
        }
        tracked.visibilityStartTime = null;
    }, []);

    const trackImpression = useCallback((tracked: TrackedElement) => {
        const { listing, config } = tracked;
        const dealType = evaluateDealType(listing.market_status?.price_ratio || 0);

        logEvent(user, EVENT_TYPES.LISTING_IMPRESSION, {
            listing_id: listing._id,
            vendor: listing.vendor,
            brand: listing.product?.brand,
            model: listing.product?.model,
            price: listing.current_price,
            deal_type: dealType,
            condition: listing.condition_name,
            threshold_used: config.threshold,
            min_visible_time: config.minVisibleTime,
            has_affiliate_link: !!listing.affiliate_listing_url,
            timestamp: Date.now(),
            ...config.metadata,
        });
    }, [user]);

    const startVisibilityTimer = useCallback((tracked: TrackedElement) => {
        if (tracked.visibilityTimer || tracked.hasTracked) return;

        tracked.visibilityStartTime = Date.now();
        tracked.visibilityTimer = setTimeout(() => {
            if (!tracked.hasTracked) {
                trackImpression(tracked);
                tracked.hasTracked = true;
            }
        }, tracked.config.minVisibleTime);
    }, [trackImpression]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const tracked = trackedElementsRef.current.get(entry.target as HTMLElement);
                    if (!tracked) return;

                    const meetsThreshold = entry.intersectionRatio >= tracked.config.threshold;

                    if (entry.isIntersecting && meetsThreshold && !tracked.hasTracked) {
                        startVisibilityTimer(tracked);
                    } else if (!entry.isIntersecting) {
                        clearVisibilityTimer(tracked);
                    }
                });
            },
            { threshold: INTERSECTION_THRESHOLDS }
        );

        observerRef.current = observer;
        const trackedElementsMap = trackedElementsRef.current;

        return () => {
            trackedElementsMap.forEach((tracked, element) => {
                clearVisibilityTimer(tracked);
                observer.unobserve(element);
            });
            trackedElementsMap.clear();
            observer.disconnect();
        };
    }, [clearVisibilityTimer, startVisibilityTimer]);

    const untrackElement = useCallback((element: HTMLElement) => {
        const tracked = trackedElementsRef.current.get(element);
        if (tracked) {
            clearVisibilityTimer(tracked);
            trackedElementsRef.current.delete(element);
            observerRef.current?.unobserve(element);
        }
    }, [clearVisibilityTimer]);

    const trackElement = useCallback((
        element: HTMLElement,
        listing: Listing,
        config: ImpressionConfig
    ): () => void => {
        if (!observerRef.current) return () => { };

        if (trackedElementsRef.current.has(element)) {
            return () => untrackElement(element);
        }

        const tracked: TrackedElement = {
            element,
            listing,
            config,
            visibilityTimer: null,
            visibilityStartTime: null,
            hasTracked: false,
        };

        trackedElementsRef.current.set(element, tracked);
        observerRef.current.observe(element);

        return () => untrackElement(element);
    }, [untrackElement]);

    const getTrackedElementsCount = useCallback(() => {
        return trackedElementsRef.current.size;
    }, []);

    return (
        <GlobalImpressionContext.Provider value={{ trackElement, getTrackedElementsCount }}>
            {children}
        </GlobalImpressionContext.Provider>
    );
}

export function useGlobalImpressionTracking() {
    const context = useContext(GlobalImpressionContext);
    if (context === undefined) {
        throw new Error('useGlobalImpressionTracking must be used within a GlobalImpressionProvider');
    }
    return context;
}